import Button, { IButton } from "../Button/Button";
import { InputAdornment, Slider, styled } from "@mui/material";
import { useContext, useState } from "react";

import { BNBox, BNInput } from "@bn-monorepo/js-packages";
import { NumericFormat } from "react-number-format";
import RichText from "../../../../CmsComponents/RichText/RichText";
import { SettingsContext } from "../../../../../settingsContext";

interface Mark {
  value: number;
  label: string;
}

export enum SliderWithInputSizes {
  Medium = "medium",
  Large = "large",
}

export interface ISliderWithInput {
  min: number;
  max: number;
  default: number;
  unitSymbol?: string;
  button?: IButton;
  queryParameterName?: string;
  step: number;
  ariaLabel: string;
  richText?: string;
  marks?: boolean | Mark[];
  title?: string;
  decimals?: number;
  adornmentPosition?: "start" | "end";
  size: SliderWithInputSizes;
  centered?: boolean;
  buttonClickHandler?: (newValue) => void;
  onChangeHandler?: (newValue) => void;
}

const BNSlider = styled(Slider)(() => ({
  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    backgroundColor: "#003D50",
    border: "0.2rem solid transparent",
    marginTop: 4,
    marginLeft: 0,
    "&:focus, &:hover, &.Mui-active": {
      boxShadow: "inherit",
    },
  },
  "& .MuiSlider-track": {
    height: 8,
    border: 0,
    backgroundColor: "transparent",
  },
  "& .MuiSlider-mark": {
    height: 8,
    width: 8,
    borderRadius: 4,
    transform: "translateX(-50%)",
    color: "#003D50",
  },
  "& .MuiSlider-markLabel": {
    lineHeight: 3,
  },
  "& .MuiSlider-markActive": {
    backgroundColor: "#003D50",
  },
  "& .MuiSlider-rail": {
    height: 14,
    borderRadius: 7,
    transform: "translateY(-0.3rem)",
    marginLeft: -7,
    width: "calc(100% + 1.4rem)",
  },
}));

const Component: React.FC<ISliderWithInput> = (props) => {
  const {
    ariaLabel,
    min,
    max,
    default: defaultValue,
    unitSymbol,
    button,
    queryParameterName,
    richText,
    step,
    marks,
    title,
    adornmentPosition,
    size,
    centered,
    buttonClickHandler,
    onChangeHandler,
  } = props;

  const { locale } = useContext(SettingsContext);

  const initialValue = typeof defaultValue === "number" ? defaultValue : (min - max) / 2;

  const [sliderValue, setSliderValue] = useState(initialValue);

  const sliderChanged = (_: any, newValue: any) => {
    const value = isNaN(newValue) ? 0 : Number(newValue);
    setSliderValue(value);
    onChangeHandler?.(value);
  };

  const inputChanged = (newValue: string) => {
    const anyValue = newValue.replace(" ", "") as any;
    const numValue = isNaN(anyValue) ? 0 : Number(anyValue);
    setSliderValue(numValue);
    onChangeHandler?.(numValue);
  };

  const valuetext = (value) => {
    return `${value} ${unitSymbol}`;
  };

  const format = (value: number, maxDecimals: number = 0) =>
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: maxDecimals,
    }).format(value);

  const inputProps = () => {
    if (adornmentPosition) {
      if (adornmentPosition === "start") {
        return {
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                "& p": {
                  fontSize: (size as SliderWithInputSizes) === SliderWithInputSizes.Large ? "2.4rem" : "1.6rem",
                },
              }}
              disablePointerEvents={true}
            >
              {unitSymbol}
            </InputAdornment>
          ),
        };
      } else {
        return {
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{
                "& p": {
                  fontSize: (size as SliderWithInputSizes) === SliderWithInputSizes.Large ? "2.4rem" : "1.6rem",
                },
              }}
              disablePointerEvents={true}
            >
              {unitSymbol}
            </InputAdornment>
          ),
        };
      }
    }
    return undefined;
  };
  return (
    <BNBox
      sx={{
        textAlign: centered ? "center" : undefined,
      }}
    >
      <div>
        <div>
          <NumericFormat
            value={sliderValue}
            thousandSeparator=" "
            onChange={(e) => inputChanged(e.target.value)}
            customInput={BNInput}
            label={title}
            type="tel"
            fullWidth={false}
            InputProps={inputProps()}
            sx={{
              "& .MuiInputBase-input": {
                textAlign: "end",
                height: "2rem",
              },
            }}
          />
          <div style={{ margin: 10 }} />
        </div>
      </div>
      <BNSlider
        aria-label={ariaLabel}
        getAriaValueText={valuetext}
        min={min}
        max={max}
        step={step}
        value={sliderValue}
        marks={marks}
        onChange={sliderChanged}
        color="secondary"
      />
      {marks == null && (
        <BNBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>{format(min)}</div>
          <div>{format(max)}</div>
        </BNBox>
      )}
      {richText && <RichText html={richText} />}

      {button && (
        <Button
          {...button}
          href={buttonClickHandler ? "" : `${button.href}?${queryParameterName}=${sliderValue}`}
          onClick={buttonClickHandler ? () => buttonClickHandler(sliderValue) : undefined}
        />
      )}
    </BNBox>
  );
};

export default Component;
